import { config } from '@/config';
import { IParamsApi } from '@/types/apis/params';
import { IResponseApi } from '@/types/apis/response';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { TagTypesKey } from '@/constants/api';
import customBaseQuery from './fetchBase';
import { IShopifyAppMetafieldPayload } from '@/types/components/metafield';
import { ROLE_SHOP_THEME } from '@/constants/enum';

const params = {
  shop: config.shop,
};

export const apiCaller = createApi({
  reducerPath: 'apiCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: customBaseQuery(),
  tagTypes: Object.values(TagTypesKey),
  endpoints: (builder) => ({
    getGeneralSetting: builder.query<IResponseApi.IGetGeneralSettings, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `shop/general-setting`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: [{ type: TagTypesKey.GENERAL_SETTING }],
    }),
    getCrispData: builder.query<IResponseApi.IGetCrispData, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `shop/crisp/data`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getShopThemes: builder.query<IResponseApi.IGetShopThemes[], IParamsApi.IGetShopThemes>({
      query: (input) => {
        const { cache, ...rest } = input;
        return {
          url: `shop/themes`,
          method: 'GET',
          params: { ...params, ...rest },
          cache: cache,
        };
      },
    }),
    getActiveEmbedApp: builder.query<IResponseApi.IGetActiveEmbedApp, IParamsApi.IGetShopThemes>({
      query: (input) => {
        const { cache, ...rest } = input;
        return {
          url: `shop/themes`,
          method: 'GET',
          params: { ...rest },
          cache,
        };
      },
      transformResponse: (response: IResponseApi.IGetShopThemes[]) => {
        const appEmbedId = process.env.REACT_APP_SHOPIFY_THEME_APP_EXTENSION_ID;

        const mainTheme = response.find((item) => item.role === ROLE_SHOP_THEME.MAIN);

        let themeId: string | undefined;
        let isActiveEmbed = false;

        if (mainTheme) {
          const idParts = mainTheme.id.split('/');
          themeId = idParts[idParts.length - 1];

          const settingsFile = mainTheme.files.find((item) => item.filename === 'config/settings_data.json')?.file;

          const blocks = settingsFile?.current?.blocks;

          if (blocks && appEmbedId) {
            const appEmbedBlock = Object.values(blocks).find((block: any) => block.type.includes(appEmbedId));
            if (appEmbedBlock) {
              isActiveEmbed = !appEmbedBlock.disabled;
            }
          }
        }
        return {
          success: true,
          message: 'Parsed embed app config',
          data: {
            themeId,
            isActiveEmbed,
          },
        };
      },
    }),
    getOrderLocations: builder.query<IResponseApi.IGetOrdersLocation, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `setting/location/filter`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getOrdersCount: builder.query<IResponseApi.IGetOrdersCount, IParamsApi.IGetOrdersCount>({
      query: (input) => {
        return {
          url: `order/report`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    checkGoogleApiKey: builder.query<IResponseApi.ICheckGoogleApiKey, IParamsApi.ICheckGoogleApiKey>({
      query: (input) => {
        return {
          url: `shop/check-google-api-key`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    checkGoogleRoutesApiKey: builder.query<IResponseApi.ICheckGoogleApiKey, IParamsApi.ICheckGoogleApiKey>({
      query: (input) => {
        return {
          url: `shop/check-google-routes-key`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getLocations: builder.query<IResponseApi.IGetListLocations, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `setting/locations`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getDetailLocation: builder.query<IResponseApi.IGetDetailLocation, IParamsApi.ICommonWithId>({
      query: ({ id, ...input }) => {
        return {
          url: `setting/location/detail/${id}`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getDetailLocationSetting: builder.query<IResponseApi.IGetDetailLocationSetting, IParamsApi.ICommonWithId>({
      query: ({ id, ...input }) => {
        return {
          url: `setting/setting/detail/${id}`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getProductsAndCollections: builder.query<IResponseApi.IGetProductsAndCollections, IParamsApi.IGetProductsAndCollections>({
      query: (input) => {
        return {
          url: `attribute/products-collections/search`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getPostalCodeValidation: builder.query<IResponseApi.IGetPostalCodeValidation, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `setting/setting/postal-code-validation`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getAppearanceSetting: builder.query<IResponseApi.IGetWidgetAppearance, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `appearance`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getGoogleConnectUrl: builder.query<IResponseApi.IGetGoogleConnectUrl, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `google/connect-url`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getGoogleAccount: builder.query<IResponseApi.IGetGoogleAccount, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `google/account`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: [{ type: TagTypesKey.GOOGLE_ACCOUNT }],
    }),
    getChargeUrl: builder.query<IResponseApi.IGetChargeUrl, IParamsApi.IGetChargeUrl>({
      query: (input) => {
        return {
          url: `shop/charge`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getPricing: builder.query<IResponseApi.IGetPricing, void>({
      query: () => {
        return {
          url: `app-pricing`,
          method: 'GET',
        };
      },
    }),
    getOrderQuota: builder.query<IResponseApi.IGetOrderQuota, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `order/quota`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getCurrentDiscountCode: builder.query<IResponseApi.IGetCurrentDiscountCode, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `app-pricing/shop-discount`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    updateDetailLocationSetting: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateDetailLocationSetting>({
      query: (body) => ({
        url: `setting/setting/update`,
        method: 'PUT',
        body,
      }),
    }),
    updateSetupGuide: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateSetupGuide>({
      query: (body) => ({
        url: `shop/setup-guide`,
        method: 'PUT',
        body,
      }),
    }),
    updateGeneralSetting: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateGeneralSetting>({
      query: (body) => ({
        url: `shop/general-setting/update`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: TagTypesKey.GENERAL_SETTING }],
    }),
    updateLocation: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateLocation>({
      query: (body) => ({
        url: `setting/location/update`,
        method: 'PUT',
        body,
      }),
    }),
    updateWidgetSetting: builder.mutation<IResponseApi.ICommonResponse, Partial<IParamsApi.IUpdateWidgetSetting>>({
      query: (body) => ({
        url: `appearance/update`,
        method: 'PUT',
        body,
      }),
    }),
    updateDiscountCode: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateDiscountCode>({
      query: (body) => ({
        url: `app-pricing/apply-code`,
        method: 'PUT',
        body,
      }),
    }),
    updateEnableSchedule: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateEnableSchedule>({
      query: (body) => ({
        url: `setting/enable-schedule`,
        method: 'PUT',
        body,
      }),
    }),
    createLocation: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICreateLocation>({
      query: (body) => ({
        url: `setting/location/create`,
        method: 'POST',
        body,
      }),
    }),
    duplicateDeliveryZone: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IDuplicateZone>({
      query: (body) => ({
        url: `setting/setting/delivery-zone/duplicate`,
        method: 'POST',
        body,
      }),
    }),
    createDeliveryZone: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICreateDeliveryZone>({
      query: (body) => ({
        url: `setting/setting/delivery-zone/create`,
        method: 'POST',
        body,
      }),
    }),
    deleteLocation: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICommonWithId>({
      query: ({ id, ...input }) => ({
        url: `setting/location/delete/${id}`,
        method: 'DELETE',
        params: { ...params, ...input },
      }),
    }),
    deleteDeliveryZone: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICommonWithId>({
      query: ({ id, ...input }) => ({
        url: `setting/setting/delivery-zone/delete/${id}`,
        method: 'DELETE',
        params: { ...params, ...input },
      }),
    }),
    deleteGoogleAccount: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `google/disconnect`,
          method: 'DELETE',
          params: { ...params, ...input },
        };
      },
      invalidatesTags: [{ type: TagTypesKey.GOOGLE_ACCOUNT }],
    }),
    getDetailSettingShipping: builder.query<IResponseApi.IGetDetailShippingSetting, void>({
      query: () => ({
        url: `setting/setting/shipping/detail`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: [{ type: TagTypesKey.SHIPPING }],
    }),
    updateDetailSettingShipping: builder.mutation<
      IResponseApi.IGetDetailShippingSetting,
      IParamsApi.IUpdateDetailShippingSetting
    >({
      query: (body) => ({
        url: `setting/setting/shipping/update`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: TagTypesKey.SHIPPING }],
    }),
    getOrderReportSummary: builder.mutation<IResponseApi.IGetReportSummary, IParamsApi.IGetReportSummary>({
      query: (body) => ({
        url: `order/report-summary`,
        method: 'POST',
        body,
      }),
    }),
    exportOrderReportCSV: builder.mutation<IResponseApi.IExportOrderReportCSV, IParamsApi.IExportOrderReportCSV>({
      query: (body) => ({
        url: `order/export`,
        method: 'POST',
        body,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { file: blob };
        },
        responseType: 'blob',
      }),
    }),
    exportProductSummary: builder.mutation<IResponseApi.IExportProductSummary, IParamsApi.IExportProductSummary>({
      query: (body) => ({
        url: `order/export-product-summary`,
        method: 'POST',
        body,
        responseHandler: async (response) => {
          const blob = await response.blob();
          return { file: blob };
        },
        responseType: 'blob',
      }),
    }),
    updateStatusOrder: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateOrderStatus>({
      query: (body) => ({
        url: `order/update-status`,
        method: 'PUT',
        body,
      }),
    }),
    createDraftOrder: builder.mutation<IResponseApi.ICreateDraftOrder, IParamsApi.ICreateDraftOrder>({
      query: (body) => ({
        url: `draft-order/create`,
        method: 'POST',
        body,
      }),
    }),
    getWidgetAppearanceText: builder.query<IResponseApi.IGetWidgetAppearanceText, IParamsApi.IGetWidgetAppearanceText>({
      query: (input) => {
        return {
          url: `appearance/text`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getSupportCheckoutPageWidgetText: builder.query<IResponseApi.IGetSupportCheckoutPage, IParamsApi.IGetSupportCheckoutPage>({
      query: (input) => {
        return {
          url: `appearance/text/support-checkout-page`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getWidgetAppearanceTextDetail: builder.query<
      IResponseApi.IGetWidgetAppearanceTextDetail,
      IParamsApi.IGetWidgetAppearanceTextDetail
    >({
      query: (input) => {
        return {
          url: `appearance/text/detail`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
      providesTags: [TagTypesKey.WIDGET_APPEARANCE_TEXT_DETAIL],
    }),
    createAppearanceText: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ICreateAppearanceText>({
      query: (body) => ({
        url: `appearance/text`,
        method: 'POST',
        body,
      }),
    }),
    updateAppearanceText: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateAppearanceText>({
      query: (body) => ({
        url: `appearance/text`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [{ type: TagTypesKey.FULL_TRANSLATION_TEXT }, { type: TagTypesKey.WIDGET_APPEARANCE_TEXT_DETAIL }],
    }),
    updateAppearanceTextDefault: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateDefaultAppearanceText>({
      query: (body) => ({
        url: `appearance/text/default`,
        method: 'PUT',
        body,
      }),
    }),
    deleteAppearanceText: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IDeleteAppearanceText>({
      query: (body) => ({
        url: `appearance/text`,
        method: 'DELETE',
        body,
      }),
    }),
    updateAppearanceTextStatus: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateAppearanceTextStatus>({
      query: (body) => ({
        url: `appearance/text/status`,
        method: 'PUT',
        body,
      }),
    }),
    getAppearanceLanguageRecommend: builder.query<IResponseApi.IGetRecommendLanguage, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `appearance/text/recommend`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    updateRevertText: builder.mutation<IResponseApi.IGetWidgetAppearanceTextDetail, IParamsApi.IRevertText>({
      query: (body) => ({
        url: `appearance/text/revert-text`,
        method: 'POST',
        body,
      }),
    }),
    getShopMetafields: builder.query<IShopifyAppMetafieldPayload, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `shop/metafields`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getTranslateText: builder.mutation<IResponseApi.IGetTranslateText, IParamsApi.IGetTranslateText>({
      query: (body) => ({
        url: `appearance/text/translate`,
        method: 'POST',
        body,
      }),
    }),
    getCannyToken: builder.query<IResponseApi.IGetCannyToken, IParamsApi.ICommonParams>({
      query: (input) => {
        return {
          url: `shop/canny-token`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getOrderAnalytics: builder.mutation<IResponseApi.IGetOrderAnalytics, IParamsApi.IGetOrderAnalytics>({
      query: (body) => ({
        url: `order/analytics`,
        method: 'POST',
        body,
      }),
    }),
    getEmailSettings: builder.query<IResponseApi.IGetEmailSettings, void>({
      query: () => ({
        url: `email`,
        method: 'GET',
        params: { ...params },
        providesTags: [{ type: TagTypesKey.EMAIL }],
      }),
    }),
    updateEmailSettings: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateEmailSettings>({
      query: (body) => ({
        url: `email`,
        method: 'PUT',
        body: { ...params, ...body },
      }),
      invalidatesTags: [{ type: TagTypesKey.EMAIL }],
    }),
    getEmailDetail: builder.query<IResponseApi.IGetEmailDetail, IParamsApi.IGetEmailDetail>({
      query: (input) => ({
        url: `email/detail`,
        method: 'GET',
        params: { ...params, ...input },
      }),
    }),
    updateEmailDetail: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IUpdateEmailDetail>({
      query: (body) => ({
        url: `email/detail`,
        method: 'PUT',
        body: { ...params, ...body },
      }),
    }),
    sendTestEmail: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ISendTestEmail>({
      query: (body) => ({
        url: `email/send-test`,
        method: 'POST',
        body: { ...params, ...body },
      }),
    }),
    sendEmail: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ISendEmail>({
      query: (body) => ({
        url: `email/send`,
        method: 'POST',
        body: { ...params, ...body },
      }),
    }),
    verifyEmail: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.IVerifyEmail>({
      query: (body) => ({
        url: `email/verify`,
        method: 'POST',
        body: { ...params, ...body },
      }),
    }),
    uploadBrandFile: builder.mutation<IResponseApi.IBrandUpload, IParamsApi.IBrandUpload>({
      query: ({ file, shop }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('shop', shop);

        return {
          url: 'file/shop',
          method: 'POST',
          body: formData,
        };
      },
    }),
    getFullTextTranslation: builder.query<IResponseApi.IGetFullTextTranslation, IParamsApi.ICommonParams>({
      query: (input) => ({
        url: `appearance/text-content`,
        method: 'GET',
        params: { ...params, ...input },
      }),
      providesTags: [{ type: TagTypesKey.FULL_TRANSLATION_TEXT }],
    }),
    updateLocationPosition: builder.mutation<IResponseApi.ICommonResponse, IParamsApi.ILocationPosition>({
      query: (body) => ({
        url: `setting/locations/position`,
        method: 'POST',
        body: { ...params, ...body },
      }),
    }),
    getCheckoutProfile: builder.query<IResponseApi.IGetCheckoutProfile, IParamsApi.ICommonParams>({
      query: (input) => ({
        url: `shop/checkout-profile`,
        method: 'GET',
        params: { ...params, ...input },
      }),
    }),
  }),
});

export const {
  useGetCrispDataQuery,
  useGetShopThemesQuery,
  useGetOrderLocationsQuery,
  useGetOrdersCountQuery,
  useLazyCheckGoogleApiKeyQuery,
  useLazyCheckGoogleRoutesApiKeyQuery,
  useGetGeneralSettingQuery,
  useGetLocationsQuery,
  useGetDetailLocationQuery,
  useGetDetailLocationSettingQuery,
  useGetPostalCodeValidationQuery,
  useGetAppearanceSettingQuery,
  useGetProductsAndCollectionsQuery,
  useGetGoogleConnectUrlQuery,
  useGetGoogleAccountQuery,
  useLazyGetChargeUrlQuery,
  useGetPricingQuery,
  useGetOrderQuotaQuery,
  useCreateLocationMutation,
  useDuplicateDeliveryZoneMutation,
  useCreateDeliveryZoneMutation,
  useUpdateSetupGuideMutation,
  useUpdateGeneralSettingMutation,
  useUpdateLocationMutation,
  useUpdateDetailLocationSettingMutation,
  useUpdateWidgetSettingMutation,
  useDeleteLocationMutation,
  useDeleteDeliveryZoneMutation,
  useDeleteGoogleAccountMutation,
  useGetDetailSettingShippingQuery,
  useUpdateDetailSettingShippingMutation,
  useUpdateDiscountCodeMutation,
  useUpdateEnableScheduleMutation,
  useGetCurrentDiscountCodeQuery,
  useGetOrderReportSummaryMutation,
  useExportOrderReportCSVMutation,
  useExportProductSummaryMutation,
  useUpdateStatusOrderMutation,
  useCreateDraftOrderMutation,
  useGetWidgetAppearanceTextQuery,
  useGetSupportCheckoutPageWidgetTextQuery,
  useGetWidgetAppearanceTextDetailQuery,
  useCreateAppearanceTextMutation,
  useUpdateAppearanceTextMutation,
  useUpdateAppearanceTextDefaultMutation,
  useDeleteAppearanceTextMutation,
  useUpdateAppearanceTextStatusMutation,
  useGetAppearanceLanguageRecommendQuery,
  useUpdateRevertTextMutation,
  useGetShopMetafieldsQuery,
  useGetTranslateTextMutation,
  useGetCannyTokenQuery,
  useGetOrderAnalyticsMutation,
  useGetActiveEmbedAppQuery,
  useGetEmailSettingsQuery,
  useUpdateEmailSettingsMutation,
  useGetEmailDetailQuery,
  useSendTestEmailMutation,
  useSendEmailMutation,
  useUploadBrandFileMutation,
  useUpdateEmailDetailMutation,
  useVerifyEmailMutation,
  useGetFullTextTranslationQuery,
  useUpdateLocationPositionMutation,
  useLazyGetAppearanceSettingQuery,
  useGetCheckoutProfileQuery
} = apiCaller;

export const apiLocationCaller = createApi({
  reducerPath: 'apiLocationCaller',
  refetchOnMountOrArgChange: 30,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_LOCATION_API_END_POINT,
  }),
  tagTypes: [],
  endpoints: (builder) => ({
    getListCountries: builder.query<IResponseApi.IGetListCountries, IParamsApi.IGetListCountries>({
      query: (input) => {
        return {
          url: `location/country/search`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
    getListCities: builder.query<IResponseApi.IGetListCities, IParamsApi.IGetListCities>({
      query: (input) => {
        return {
          url: `location/state/search`,
          method: 'GET',
          params: { ...params, ...input },
        };
      },
    }),
  }),
});

export const { useGetListCountriesQuery, useGetListCitiesQuery } = apiLocationCaller;
